import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isValid as isDateValid, parse as dfParse } from 'date-fns';
import { isNil } from 'lodash';

const SEGMENT_LIMITS = {
  day: { min: 1, max: 31 },
  month: { min: 1, max: 12 },
  year: { min: 1, max: 9999 },
};
const SEPARATOR_KEYS = ['/', '-', '.', ' '];
const PASSTHROUGH_KEYS = ['Tab', 'Enter', 'Escape'];

function tokenizeDateFormat(dateFormat) {
  const parts = [];
  let index = 0;

  while (index < dateFormat.length) {
    const char = dateFormat[index];
    if (!/[A-Za-z]/.test(char)) {
      parts.push({ kind: 'sep', value: char });
      index += 1;
    } else {
      let token = char;
      while (dateFormat[index + 1] === char) {
        token += char;
        index += 1;
      }
      let key = 'day';
      if (char.toLowerCase() === 'y') key = 'year';
      if (char === 'M') key = 'month';
      parts.push({ kind: 'field', key, token, size: token.length });
      index += 1;
    }
  }

  return parts;
}

function emptyBuffers(fields) {
  return fields.reduce((buffers, field) => ({ ...buffers, [field.key]: '' }), {});
}

function getDayIndex(fields) {
  const dayIndex = fields.findIndex((field) => field.key === 'day');
  return dayIndex === -1 ? 0 : dayIndex;
}

function buffersFromDate(date) {
  return { day: String(date.getDate()), month: String(date.getMonth() + 1), year: String(date.getFullYear()) };
}

// "0" alone is not a value yet, so keep the rest of the token visible (0d, 0m) instead of "00".
function fieldText(buffers, field) {
  const digits = buffers[field.key];
  const token = field.token.toLowerCase();
  if (!digits) return token;
  if (field.key === 'year' && digits.length < field.size) return `${digits}${token.slice(digits.length)}`;
  if (Number(digits) === 0) return `${digits}${token.slice(digits.length)}`;
  return digits.padStart(field.size, '0');
}

function fieldPieces(buffers, field, partText) {
  const digits = buffers[field.key];
  if (!digits) return [{ text: partText, isPlaceholder: true }];
  if (field.key !== 'year' && Number(digits) !== 0) return [{ text: partText, isPlaceholder: false }];
  if (digits.length >= field.size) return [{ text: partText, isPlaceholder: false }];
  return [
    { text: digits, isPlaceholder: false },
    { text: partText.slice(digits.length), isPlaceholder: true },
  ];
}

function buildLayout(parts, buffers) {
  let position = 0;
  let text = '';
  const ranges = [];
  const pieces = [];
  const isEmpty = parts.every((part) => part.kind === 'sep' || !buffers[part.key]);

  parts.forEach((part) => {
    const partText = part.kind === 'sep' ? part.value : fieldText(buffers, part);
    if (part.kind === 'field') {
      ranges.push({ start: position, end: position + partText.length });
      pieces.push(...fieldPieces(buffers, part, partText));
    } else {
      pieces.push({ text: partText, isPlaceholder: isEmpty });
    }
    text += partText;
    position += partText.length;
  });

  return { text, ranges, pieces: pieces.filter((piece) => piece.text) };
}

function evaluateBuffers(fields, buffers, text, dateFormat, isRequired) {
  if (fields.every((field) => !buffers[field.key])) {
    return { status: isRequired ? 'invalid' : 'empty', date: null };
  }

  const parsed = dfParse(text, dateFormat, new Date());
  if (!isDateValid(parsed)) return { status: 'invalid', date: null };
  return { status: 'complete', date: parsed };
}

function typeDigit(buffers, field, digit, isContinuing) {
  const limits = SEGMENT_LIMITS[field.key];
  const current = isContinuing ? buffers[field.key] : '';
  let digits = current.length >= field.size ? digit : `${current}${digit}`;

  if (field.key !== 'year' && Number(digits) > limits.max) digits = digit;
  if (digits.length === field.size && Number(digits) < limits.min) {
    return { buffers: { ...buffers, [field.key]: '' }, shouldAdvance: false };
  }

  const isFull = digits.length >= field.size || (field.key !== 'year' && Number(digits) * 10 > limits.max);
  return { buffers: { ...buffers, [field.key]: digits }, shouldAdvance: isFull };
}

function stepSegment(buffers, field, delta) {
  const limits = SEGMENT_LIMITS[field.key];
  const today = buffersFromDate(new Date());
  const current = buffers[field.key] ? Number(buffers[field.key]) : null;
  let next = current === null ? Number(today[field.key]) : current + delta;

  if (field.key === 'year') next = Math.min(Math.max(next, limits.min), limits.max);
  else if (next > limits.max) next = limits.min;
  else if (next < limits.min) next = limits.max;

  return { ...buffers, [field.key]: String(next) };
}

function buffersFromPaste(text, fields, dateFormat) {
  const parsed = dfParse(text.trim(), dateFormat, new Date());
  if (isDateValid(parsed)) return buffersFromDate(parsed);

  const digits = text.replace(/\D/g, '');
  const totalSize = fields.reduce((sum, field) => sum + field.size, 0);
  if (digits.length !== totalSize) return null;

  let offset = 0;
  return fields.reduce((buffers, field) => {
    const chunk = digits.slice(offset, offset + field.size);
    offset += field.size;
    return { ...buffers, [field.key]: String(Number(chunk)) };
  }, {});
}

/**
 * Segmented date typing for a single text field (day / month / year in one input, one tab stop).
 * + empty segment shows its format token: dd/11/2026, 22/mm/2026
 * + typed digits are padded with "0" and focus moves on when the segment cannot take another digit
 * + Backspace removes digits; an emptied segment shows its token again, never "00"
 * + ArrowLeft / ArrowRight move between segments, ArrowUp / ArrowDown step the active segment
 * + any non-empty text is validated by date-fns parse/isValid: onCommit when valid, onInvalid otherwise
 */
export default function useEnhanceDateInput({ dateFormat, value, isRequired, onCommit, onClear, onInvalid }) {
  const parts = useMemo(() => tokenizeDateFormat(dateFormat), [dateFormat]);
  const fields = useMemo(() => parts.filter((part) => part.kind === 'field'), [parts]);

  const [buffers, setBuffers] = useState(() => emptyBuffers(fields));
  const [activeIndex, setActiveIndex] = useState(() => getDayIndex(fields));
  const [isInvalid, setIsInvalid] = useState(false);

  const inputRef = useRef(null);
  const isContinuingRef = useRef(false);
  const buffersRef = useRef(buffers);
  const callbacksRef = useRef({ onCommit, onClear, onInvalid });
  callbacksRef.current = { onCommit, onClear, onInvalid };
  buffersRef.current = buffers;

  const { text, ranges, pieces } = buildLayout(parts, buffers);

  function selectSegment(index) {
    const input = inputRef.current;
    const range = ranges[index];
    if (!input || !range || document.activeElement !== input) return;
    input.setSelectionRange(range.start, range.end);
  }

  useLayoutEffect(() => {
    selectSegment(activeIndex);
  }, [text, activeIndex]);

  useEffect(() => {
    if (isDateValid(value)) {
      const nextBuffers = buffersFromDate(value);
      if (buildLayout(parts, nextBuffers).text !== buildLayout(parts, buffersRef.current).text) {
        setBuffers(nextBuffers);
      }
      setIsInvalid(false);
      return;
    }

    const { current } = buffersRef;
    const currentStatus = evaluateBuffers(
      fields,
      current,
      buildLayout(parts, current).text,
      dateFormat,
      isRequired,
    ).status;
    if (isNil(value) && currentStatus === 'complete') setBuffers(emptyBuffers(fields));
  }, [value, parts, fields, dateFormat, isRequired]);

  function applyChange(nextBuffers, nextActiveIndex) {
    setBuffers(nextBuffers);
    setActiveIndex(nextActiveIndex);

    const nextText = buildLayout(parts, nextBuffers).text;
    const result = evaluateBuffers(fields, nextBuffers, nextText, dateFormat, isRequired);
    const { onCommit: commit, onClear: clear, onInvalid: invalid } = callbacksRef.current;

    setIsInvalid(result.status === 'invalid');
    if (result.status === 'empty' && clear) clear();
    if (result.status === 'invalid' && invalid) invalid();
    if (result.status === 'complete' && commit) commit(result.date);
  }

  function moveTo(index) {
    const nextIndex = Math.min(Math.max(index, 0), fields.length - 1);
    isContinuingRef.current = false;
    setActiveIndex(nextIndex);
    selectSegment(nextIndex);
  }

  function handleDigit(digit) {
    const field = fields[activeIndex];
    const typed = typeDigit(buffers, field, digit, isContinuingRef.current);
    const isLastField = activeIndex === fields.length - 1;
    isContinuingRef.current = !typed.shouldAdvance || isLastField;
    applyChange(typed.buffers, typed.shouldAdvance && !isLastField ? activeIndex + 1 : activeIndex);
  }

  function handleBackspace() {
    const field = fields[activeIndex];
    const digits = buffers[field.key];
    if (!digits) {
      moveTo(activeIndex - 1);
      return;
    }
    isContinuingRef.current = true;
    applyChange({ ...buffers, [field.key]: field.key === 'year' ? '' : digits.slice(0, -1) }, activeIndex);
  }

  function handleKeyDown(event) {
    if (event.ctrlKey || event.metaKey || event.altKey || PASSTHROUGH_KEYS.includes(event.key)) return;
    event.preventDefault();

    const field = fields[activeIndex];
    if (/^\d$/.test(event.key)) {
      handleDigit(event.key);
      return;
    }
    if (SEPARATOR_KEYS.includes(event.key)) {
      if (buffers[field.key]) moveTo(activeIndex + 1);
      return;
    }

    switch (event.key) {
      case 'Backspace':
        handleBackspace();
        break;
      case 'Delete':
        isContinuingRef.current = false;
        applyChange({ ...buffers, [field.key]: '' }, activeIndex);
        break;
      case 'ArrowLeft':
        moveTo(activeIndex - 1);
        break;
      case 'ArrowRight':
        moveTo(activeIndex + 1);
        break;
      case 'ArrowUp':
        isContinuingRef.current = false;
        applyChange(stepSegment(buffers, field, 1), activeIndex);
        break;
      case 'ArrowDown':
        isContinuingRef.current = false;
        applyChange(stepSegment(buffers, field, -1), activeIndex);
        break;
      case 'Home':
        moveTo(0);
        break;
      case 'End':
        moveTo(fields.length - 1);
        break;
      default:
        break;
    }
  }

  function handlePaste(event) {
    event.preventDefault();
    const pasted = buffersFromPaste(event.clipboardData.getData('text'), fields, dateFormat);
    if (!pasted) return;
    isContinuingRef.current = false;
    applyChange(pasted, fields.length - 1);
  }

  function handleFocus() {
    const index = getDayIndex(fields);
    isContinuingRef.current = false;
    setActiveIndex(index);
    requestAnimationFrame(() => selectSegment(index));
  }

  function handleMouseUp(event) {
    const caret = event.target.selectionStart;
    const index = ranges.findIndex((range) => caret <= range.end);
    moveTo(index === -1 ? fields.length - 1 : index);
  }

  return {
    text,
    pieces,
    isInvalid,
    inputRef,
    inputProps: {
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
      onFocus: handleFocus,
      onMouseUp: handleMouseUp,
      inputMode: 'numeric',
      autoComplete: 'off',
      spellCheck: false,
    },
  };
}
